import { Box, Typography } from "@mui/material";
import type { Sample, Core } from "@rogoag/shared-ts";

export const SAMPLE_TYPE_LABELS: Record<number, string> = {
	0: "Regular",
	1: "ISNT 1",
	2: "ISNT 2",
	3: "Cyst",
};

export function formatTimestamp(ts: string | number | undefined): string {
	if (!ts) return "—";
	let ms: number;
	if (typeof ts === "string") {
		ms = new Date(ts).getTime();
	} else if (ts > 1e15) {
		ms = ts / 1000;
	} else if (ts > 1e12) {
		ms = ts;
	} else {
		ms = ts * 1000;
	}
	// Catch bogus dates from upstream (e.g. microseconds parsed as ms → year 58167)
	if (ms > 1e15) ms = ms / 1000;
	const date = new Date(ms);
	if (isNaN(date.getTime()) || date.getTime() === 0) return "—";
	return date.toLocaleString();
}

export function PopupRow({ label, value }: { label: string; value: string | number | undefined }) {
	if (!value && value !== 0) return null;
	return (
		<Typography variant="body2" sx={{ fontSize: "0.75rem", lineHeight: 1.4 }}>
			<strong>{label}:</strong> {value}
		</Typography>
	);
}

export function SamplePopupContent({ sample }: { sample: Sample }) {
	const ext = sample.extendedData;
	return (
		<Box sx={{ minWidth: 180, maxWidth: 280 }}>
			<Typography variant="subtitle2" sx={{ mb: 0.5, borderBottom: 1, borderColor: "divider", pb: 0.5 }}>
				Sample {sample.id}
			</Typography>
			<PopupRow label="Sample ID" value={ext.sample_id} />
			<PopupRow label="Bag ID" value={ext.bag_id} />
			<PopupRow label="Order" value={ext.order} />
			<PopupRow label="Type" value={SAMPLE_TYPE_LABELS[ext.sample_type as number] ?? ext.sample_type} />
			<PopupRow
				label="Depth"
				value={ext.start_depth && ext.end_depth ? `${ext.start_depth}–${ext.end_depth}"` : undefined}
			/>
			<PopupRow label="Core Length" value={ext.core_length_cm ? `${ext.core_length_cm} cm` : undefined} />
			<PopupRow label="Cores" value={sample.cores?.length} />
			<PopupRow label="Lat" value={sample.coordinates?.[1]?.toFixed(6)} />
			<PopupRow label="Lng" value={sample.coordinates?.[0]?.toFixed(6)} />
			<PopupRow label="Sampled" value={formatTimestamp(ext.sampled_at)} />
			<PopupRow label="Scanned" value={formatTimestamp(ext.scanned_at)} />
			<PopupRow label="Pulled" value={formatTimestamp(ext.pulled_at)} />
			<PopupRow label="Box UID" value={ext.box_uid} />
			{ext.change_type && ext.change_type !== "None" && (
				<PopupRow label="Change" value={`${ext.change_type}${ext.change_reason ? `: ${ext.change_reason}` : ""}`} />
			)}
		</Box>
	);
}

export function CorePopupContent({ core }: { core: Core }) {
	const ext = core.extendedData;
	return (
		<Box sx={{ minWidth: 180, maxWidth: 280 }}>
			<Typography variant="subtitle2" sx={{ mb: 0.5, borderBottom: 1, borderColor: "divider", pb: 0.5 }}>
				Core {core.id}
			</Typography>
			<PopupRow label="Sample ID" value={ext.sample_id} />
			<PopupRow label="Bag ID" value={ext.bag_id} />
			<PopupRow label="Order" value={ext.order} />
			<PopupRow label="Type" value={SAMPLE_TYPE_LABELS[ext.sample_type as number] ?? ext.sample_type} />
			<PopupRow
				label="Depth"
				value={ext.start_depth && ext.end_depth ? `${ext.start_depth}–${ext.end_depth}"` : undefined}
			/>
			<PopupRow label="Core Length" value={ext.core_length_cm ? `${ext.core_length_cm} cm` : undefined} />
			<PopupRow label="Planned" value={`${core.plannedLatitude?.toFixed(6)}, ${core.plannedLongitude?.toFixed(6)}`} />
			<PopupRow label="Pulled" value={`${core.pulledLatitude?.toFixed(6)}, ${core.pulledLongitude?.toFixed(6)}`} />
			<PopupRow label="Sampled" value={formatTimestamp(ext.sampled_at)} />
			<PopupRow label="Scanned" value={formatTimestamp(ext.scanned_at)} />
			<PopupRow label="Pulled At" value={formatTimestamp(ext.pulled_at)} />
			<PopupRow label="Box UID" value={ext.box_uid} />
			{ext.change_type && ext.change_type !== "None" && <PopupRow label="Change" value={String(ext.change_type)} />}
		</Box>
	);
}
